'use client';

import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendSeries } from '../_lib/api';
import { useTrend } from '../_lib/hooks';

const COLORS = [
  '#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#14b8a6', '#ec4899', '#64748b', '#0ea5e9', '#84cc16',
];

type GroupBy = 'none' | 'user' | 'project' | 'tool' | 'model';

interface Props {
  period: string;
  /** 'admin' 调 /trend；'me' 调 /me/trend，groupBy 限制不暴露 user 维度 */
  scope: 'admin' | 'me';
}

export function DailyTrendChart({ period, scope }: Props) {
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [granularity, setGranularity] = useState<'day' | 'week'>('day');
  const { data: trendResp, isFetching } = useTrend(scope, { period, granularity, groupBy });
  const series: TrendSeries[] = trendResp?.series || [];
  const loading = isFetching && !trendResp;

  // 把 series 透视为 recharts data: [{ bucket, [seriesKey]: tokens, ... }]
  // 用每 series 的 bucket→tokens Map 避免 O(n²) 的 .find
  const { data, seriesKeys, labelMap } = useMemo(() => {
    const buckets = new Set<string>();
    const labelMap = new Map<string, string>();
    const idxBySeries = new Map<string, Map<string, number>>();
    for (const s of series) {
      labelMap.set(s.key, s.label || s.key);
      const m = new Map<string, number>();
      for (const p of s.points) {
        buckets.add(p.bucket);
        m.set(p.bucket, p.tokens);
      }
      idxBySeries.set(s.key, m);
    }
    const sortedBuckets = Array.from(buckets).sort();
    const data = sortedBuckets.map((b) => {
      const row: Record<string, any> = { bucket: b };
      for (const s of series) row[s.key] = idxBySeries.get(s.key)?.get(b) ?? 0;
      return row;
    });
    return { data, seriesKeys: series.map((s) => s.key), labelMap };
  }, [series]);

  const groupOptions: Array<{ value: GroupBy; label: string }> =
    scope === 'admin'
      ? [
          { value: 'none', label: '总量' },
          { value: 'user', label: '按员工' },
          { value: 'project', label: '按项目' },
          { value: 'tool', label: '按工具' },
          { value: 'model', label: '按模型' },
        ]
      : [
          { value: 'none', label: '总量' },
          { value: 'project', label: '按项目' },
          { value: 'tool', label: '按工具' },
          { value: 'model', label: '按模型' },
        ];

  return (
    <div className="bg-white rounded shadow p-4 mb-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-sm font-medium text-gray-700">每日趋势（Token）</div>
        <div className="flex gap-2 items-center">
          <select
            className="text-xs border rounded px-2 py-1"
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as any)}
          >
            <option value="day">按日</option>
            <option value="week">按周</option>
          </select>
          <select
            className="text-xs border rounded px-2 py-1"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
          >
            {groupOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading && <div className="text-xs text-gray-400">Loading…</div>}
      {!loading && data.length === 0 && (
        <div className="h-[300px] flex items-center justify-center text-xs text-gray-400">无数据</div>
      )}
      {!loading && data.length > 0 && (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtToken} />
            <Tooltip formatter={(v: any) => Number(v).toLocaleString()} />
            <Legend />
            {seriesKeys.map((k, i) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                stroke={COLORS[i % COLORS.length]}
                name={labelMap.get(k) || k}
                dot={false}
                strokeWidth={1.5}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function fmtToken(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(0) + 'k';
  return String(n);
}
